export type WeatherCurrent = {
  temperature: number;
  humidity: number;
  pressure: number;
  precipitation: number;
  windSpeed: number;
  windDirection: number;
  weatherCode: number;
};

export type WeatherHourly = {
  time: string;
  temperature: number;
  precipitationProbability: number;
  precipitation: number;
  windSpeed: number;
  weatherCode?: number;
  /** UV index. */
  uv?: number;
};

export type WeatherDaily = {
  date: string;
  tMax: number;
  tMin: number;
  sunrise: string;
  sunset: string;
  weatherCode?: number;
  precipProbMax?: number;
  precipSum?: number;
  windMax?: number;
  uvMax?: number;
};

export type WeatherSection = {
  current: WeatherCurrent | null;
  hourly: WeatherHourly[];
  daily: WeatherDaily[];
};

export type AirSection = {
  stationCode: string;
  stationName: string;
  measurements: {
    pm25: number | null;
    pm10: number | null;
    no2: number | null;
    o3: number | null;
  };
  measuredAt: string;
} | null;

export type Warning = {
  id: string;
  level: 1 | 2 | 3 | 4;
  type: string;
  headline: string;
  description: string;
  validFrom: string;
  validTo: string;
};

export type SensorSection = {
  boxId: string;
  boxName: string;
  lastReadingAt: string;
  measurements: {
    temperature: number | null;
    humidity: number | null;
    pressure: number | null;
    uv: number | null;
    lux: number | null;
    pm25: number | null;
    pm10: number | null;
  };
} | null;

/**
 * One Bavarian gauge station's current reading.
 *
 * Fields mirror what the Bavarian state services actually publish. Note what is
 * NOT here: flow velocity in m/s. Gauges publish level, discharge and
 * temperature; velocity is derived from discharge over the wetted
 * cross-section, and the cross-section is not in the public feed.
 * `dischargeCms` is the honest measure of "how much is it moving".
 */
/**
 * A gauge's live discharge set against its own long-term mean.
 *
 * Discharge only, and deliberately so: gauge level is measured above each
 * station's own arbitrary zero, which makes it an interval scale on which a
 * ratio means nothing — 92 cm at Isar/München is 23.8 m³/s while 92 cm at
 * Isar/Landshut Birket is 69.4 m³/s. Discharge has a true zero, so a percentage
 * of it is a real statement.
 */
export type FlowComparison = {
  /** Live discharge ÷ the mean for this half-year. 0.3 is "a third of normal". */
  ratio: number;
  /** MQ, the mean discharge for the half-year used, m³/s. */
  meanCms: number;
  /** MNQ, the mean of the annual lows for that half-year, m³/s. */
  lowMeanCms: number | null;
  /** Which hydrological half-year the reading fell in. */
  season: "winter" | "sommer";
  /** GKD's reference period for this station, verbatim, e.g. "1959 - 2021". */
  period: string;
  /** At or below MNQ — low even for the river's low-water season. */
  atOrBelowTypicalLow: boolean;
};

export type WaterStation = {
  /**
   * The Bavarian gauge number, e.g. "16005701". The same number identifies the
   * station in HND (level, flow) and in GKD (temperature), which is what
   * lets the two feeds be joined.
   */
  id: string;
  /** Place, as the service publishes it — e.g. "München Himmelreichbrücke". */
  name: string;
  /** The water body it sits on, e.g. "Isar" or "Tegernsee". */
  water: string;
  /** Wasserstand: level above the station's own zero, cm. */
  levelCm: number | null;
  /** Abfluss: discharge, m³/s. */
  dischargeCms: number | null;
  /** Wassertemperatur, °C. */
  temperatureC: number | null;
  /** Timestamp of the level / flow reading, ISO. */
  measuredAt: string | null;
  /** Timestamp of the temperature reading — a separate feed, so a separate clock. */
  temperatureAt: string | null;
  /** Meldestufe 1–4 when the gauge stands above a flood threshold, else null. */
  floodStage: number | null;
  /**
   * Discharge against this gauge's own long-term mean, or null when the
   * comparison cannot be made — no live flow, or no statistic published for the
   * station (every lake, and the level-only gauges).
   *
   * Optional because snapshots written before this existed carry stations
   * without it, and those are still current-shape and still carried forward.
   */
  flowVsNormal?: FlowComparison | null;
  /** HND's own fault note for the gauge ("Störung"), when it carries one. */
  outage: string | null;
};

export type WaterSection = {
  stations: WaterStation[];
  /**
   * Which of the two feeds answered this run. An empty station list means
   * something different depending on these: both false is "the lookup failed",
   * level true and temperature false is "levels are live, temperatures are not".
   */
  sources: { level: boolean; temperature: boolean };
} | null;

/**
 * Two Bavarian gauge numbers the deck refers to by name.
 *
 * Both sit in central Munich, 470 m apart (GKD master data, ETRS89/UTM 32N:
 * Isar E 693170 / N 5335733 at 500 m, Eisbach E 692715 / N 5335852 at 506 m).
 * The Eisbach is a side arm of the Isar, so the pair reads as "the channel
 * against the river that feeds it".
 */
/**
 * Is this water section the current shape, or one left over from before the
 * migration to the Bavarian state services?
 *
 * A snapshot written by the PEGELONLINE version carries stations keyed by
 * `uuid` with a `distanceKm`, a `searchRadiusKm`, and no `sources` at all. The
 * fields the card now reads are simply absent from it. Neither carrying it
 * forward nor rendering it is acceptable: a stale card is worse than an honest
 * gap, because it looks like it is working and hides the fact that the live
 * lookup is failing.
 */
export function isCurrentWaterSection(w: unknown): w is NonNullable<WaterSection> {
  if (typeof w !== "object" || w === null) return false;
  const s = w as { stations?: unknown; sources?: { level?: unknown; temperature?: unknown } };
  return Array.isArray(s.stations)
    && typeof s.sources === "object" && s.sources !== null
    && typeof s.sources.level === "boolean"
    && typeof s.sources.temperature === "boolean";
}

export const EISBACH_STATION_ID = "16515005";
export const ISAR_MUNICH_STATION_ID = "16005701";

export type Snapshot = {
  fetchedAt: string;
  weather: WeatherSection;
  air: AirSection;
  warnings: Warning[];
  sensor: SensorSection;
  /** Optional: absent on snapshots written before river/lake support. */
  water?: WaterSection;
};

export type AlertCategories = {
  storm: boolean;
  frost: boolean;
  heat: boolean;
  highPm25: boolean;
  /** senseBox mfreiheit stopped uploading — an operational alert, on by default. */
  sensorOffline: boolean;
};

/** Tracks the sensor-dropout alert in KV, decoupled from any local machine's uptime. */
export interface SensorAlertState {
  alertedAt: string | null;
  lastPingAt: string | null;
}

export type PushSubscription = {
  id: string;
  endpoint: string;
  keys: { p256dh: string; auth: string };
  subscribedAt: string;
  categories: AlertCategories;
};

export interface SeasonalBaseline {
  doy: number[];
  p10: (number | null)[];
  p50: (number | null)[];
  p90: (number | null)[];
  n_samples: number[];
}
export interface YoYMatrix {
  [year: string]: { [doy: string]: number | null };
}

/**
 * One rolling-history sample: only the fields the history consumers actually
 * read, and nothing else.
 *
 * /api/history used to return whole Snapshots — 48 hourly + 7 daily forecast
 * entries apiece, ~7.5 kB each, up to 672 of them (~4.8 MB) rebuilt from 672
 * individual KV reads on every page load. Keep this type slim: a field earns
 * its place only when a consumer reads it.
 */
export interface HistoryPoint {
  /** Snapshot time (was Snapshot.fetchedAt), ISO. */
  t: string;
  /** Rooftop temperature, °C. */
  deck: number | null;
  /**
   * When the rooftop box actually took that reading. The aggregator re-serves
   * a stale sensor block for up to 2 h, so the delta card needs this to avoid
   * pairing an old rooftop value against a fresh forecast one.
   */
  deckAt: string | null;
  /** Open-Meteo current temperature, °C. */
  dwd: number | null;
  /** Open-Meteo current pressure hPa, added for the pressure-drop insight rule. */
  p?: number | null;
}

/**
 * A day filled in from a confident nearby source when the rooftop box itself
 * had no reading — the same DWD/citizen-sensor priority chain already used to
 * build the historical aggregate offline (see scripts/merge_gapfill.py).
 */
export type GapFillSource = "dwd-stadt" | "dwd-flughafen" | "sc-7890" | "sc-4453";

/** Day-of-month means for the running month, served live from the worker's KV rollup. */
export interface MonthDailyMeans {
  year: number;
  month: number;
  metric: string;
  days: { [day: string]: number | null };
  samples: { [day: string]: number };
  sources: { [day: string]: "live" | "archive" | GapFillSource };
}
export interface RollingAnomaly {
  ts_local: string[];
  mean: number[];
  roll_mean: number[];
  roll_std: number[];
  z: number[];
}
export interface MonthlyTrend {
  monthly: { [monthISO: string]: number };
  slope_per_year: number | null;
  r2: number | null;
}
export interface HeatwaveYear {
  days_above_25C: number;
  days_above_30C: number;
  days_above_35C: number;
  longest_run_above_25C: number;
  longest_run_above_30C: number;
  longest_run_above_35C: number;
}
export interface FrostDiurnalYear {
  frost_days: number;
  mean_diurnal_range_C: number;
  n_days: number;
}
export interface SensorAggregate {
  sensor: string;
  first_day: string;
  last_day: string;
  n_days: number;
  seasonal_baseline: SeasonalBaseline;
  yoy_matrix: YoYMatrix;
  rolling_anomaly_30d: RollingAnomaly;
  monthly_trend: MonthlyTrend;
  heatwaves?: { [year: string]: HeatwaveYear };
  frost_and_diurnal?: { [year: string]: FrostDiurnalYear };
  who_exceedance?: { [year: string]: { [key: string]: number } };
  pressure_fronts_per_year?: { [year: string]: number };
  annual_dose_mJ_cm2?: { [year: string]: number };
}

export interface RecordFact {
  id: string;
  icon: string;
  headline: string;
  sub?: string;
}
export interface RecordsFile {
  first_day: string;
  last_day: string;
  note: string;
  facts: RecordFact[];
}

export interface HourlyMomentCell {
  vals: (number | null)[];
  src: (string | null)[];
}
export interface MomentMonth {
  month: number;
  years: number[];
  sensors: { [sensor: string]: { [day: string]: { [hour: string]: HourlyMomentCell } } };
}

// --- Phase 1: insight engine + day plan (spec 2026-08-16) ---

/** Rule family for a generated insight candidate. */
export type InsightCategory = "record" | "divergence" | "streak" | "air" | "rain-normal";

/** One candidate insight surfaced by the insight engine, ranked by rarity before display. */
export interface InsightCandidate {
  /** Stable rule id. */
  id: string;
  category: InsightCategory;
  headline: string;
  detail: string;
  /** 0 (common) – 100 (rare). */
  rarity: number;
  /** ISO timestamp after which this candidate should no longer be shown. */
  validUntil: string;
}

/** Classification for one hour of the day-plan ribbon. */
export type RibbonKind = "warning" | "rain" | "uv-high" | "hot" | "good-outside" | "neutral" | "night";

/** One hour's slice of the 24-hour day-plan ribbon. */
export interface RibbonSegment {
  /** Hour of day, 0–23. */
  hour: number;
  kind: RibbonKind;
  /** °C. */
  temp: number | null;
  /** Percent, 0–100. */
  precipProb: number | null;
  /** km/h. */
  wind: number | null;
  /** UV index. */
  uv: number | null;
}

/** A full day rendered as an hour-by-hour ribbon with a recommended outdoor window. */
export interface DayPlan {
  label: "Today" | "Tomorrow";
  /** Calendar date, YYYY-MM-DD. */
  date: string;
  /** Exactly 24 entries, one per hour. */
  segments: RibbonSegment[];
  /** Best outdoor window, or null if none stands out. Half-open: `from` inclusive, `to` exclusive, hours 0–24. */
  bestWindow: { from: number; to: number } | null;
  caption: string;
  /** Display copy for the day's single most important hazard, at most one clause — not a machine category. Null if none. */
  hazard: string | null;
}

/** Top-level payload served to the web app: headline insight, runners-up, and the day plan. */
export interface InsightsPayload {
  /** ISO timestamp. */
  computedAt: string;
  headline: InsightCandidate | null;
  /** Runners-up, sorted by rarity descending, max 5. */
  others: InsightCandidate[];
  dayPlan: DayPlan | null;
  /**
   * Headline ids that have recently led, newest first, max 7 entries.
   *
   * `ledOn` is the Berlin date it last led on. Without it "recent" was only a
   * count of distinct ids and carried no sense of time, so a story that was the
   * only one clearing the bar led every day indefinitely. Optional because
   * payloads written before it exists must still load; those entries fall back
   * to the older demote-but-allow behaviour.
   */
  recentHeadlines: Array<{ id: string; rarity: number; ledOn?: string }>;
}

/** One day of climatology (normal) values used to contextualize current readings. */
export interface ClimoDay {
  tMin: number | null;
  tMax: number | null;
  precipMm: number | null;
  /** "archive" = Open-Meteo historical backfill, "live" = derived from the day's own rollup bucket. */
  source: "archive" | "live";
}

/** Climatology series keyed by date, refreshed on a rolling basis. */
export interface ClimoDaily {
  /** ISO timestamp. */
  updatedAt: string;
  days: { [date: string]: ClimoDay };
}
